import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Dialog, Typography, Box } from "@mui/material";
import {
  EyebrowUpsellOffer,
  formatPrice,
  getEyebrowUpsellMessage,
} from "../../utils";
import Button from "../button";
import ModalCloseButton from "../modal-close-button";
import {
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
} from "../../styles/modal-styles";
import { THEME } from "../../styles/theme";

export interface EyebrowUpsellModalRef {
  open: (
    offer: EyebrowUpsellOffer,
    callbacks: {
      onAccept: () => void;
      onDismiss: () => void;
    }
  ) => void;
  close: () => void;
}

const EyebrowUpsellModal = forwardRef<EyebrowUpsellModalRef>((_props, ref) => {
  const [offer, setOffer] = useState<EyebrowUpsellOffer | null>(null);
  const [onAccept, setOnAccept] = useState<(() => void) | null>(null);
  const [onDismiss, setOnDismiss] = useState<(() => void) | null>(null);
  const open = !!offer;

  useImperativeHandle(ref, () => ({
    open: (offerData, callbacks) => {
      setOffer(offerData);
      setOnAccept(() => callbacks.onAccept);
      setOnDismiss(() => callbacks.onDismiss);
    },
    close: () => {
      setOffer(null);
      setOnAccept(null);
      setOnDismiss(null);
    },
  }));

  const handleClose = () => {
    setOffer(null);
    setOnAccept(null);
    setOnDismiss(null);
  };

  const handleAccept = () => {
    const callback = onAccept;
    handleClose();
    callback?.();
  };

  const handleDismiss = () => {
    const callback = onDismiss;
    handleClose();
    callback?.();
  };

  if (!offer) return null;

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "1.5rem" } }}
    >
      <StyledDialogTitle sx={{ pb: 1 }}>
        Oferta especial
        <ModalCloseButton onClose={handleDismiss} />
      </StyledDialogTitle>

      <StyledDialogContent>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${THEME.palette.primary.main}`,
            background: `linear-gradient(135deg, ${THEME.palette.primary.main}12 0%, ${THEME.palette.background.paper} 100%)`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "inline-block",
              bgcolor: "primary.main",
              color: "#fff",
              fontWeight: 600,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              mb: 1,
            }}
          >
            40% off
          </Typography>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            Inclua sobrancelha no mesmo horário
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getEyebrowUpsellMessage(offer)}
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            fontWeight={600}
            sx={{ mt: 1 }}
          >
            Por apenas +{formatPrice(offer.extraPrice)}
          </Typography>
        </Box>
      </StyledDialogContent>

      <StyledDialogActions
        sx={{
          p: 3,
          pt: 1,
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
        }}
      >
        <Button
          onClick={handleDismiss}
          variant="outlined"
          color="secondary"
          fullWidth
        >
          Não, obrigado
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          fullWidth
        >
          Aproveitar oferta
        </Button>
      </StyledDialogActions>
    </Dialog>
  );
});

EyebrowUpsellModal.displayName = "EyebrowUpsellModal";

export default EyebrowUpsellModal;
